import { ActionIcon, Box, Loader, NumberInput, Tooltip } from "@mantine/core";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import styles from "./InlineQuantityStepper.module.css";

interface InlineQuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  /** Lower bound. The decrement control disables when the next step would
   *  cross below this. Typed values are clamped to it on blur. */
  min: number;
  /** Optional upper bound. The increment control disables once the value is at
   *  (or above) this; a final partial step lands on it. Typed values are
   *  clamped to it. */
  max?: number;
  /** Amount each +/- press changes the value (e.g. 1 unit, 0.5 syringe). */
  step: number;
  /** Decimals to keep: 0 for integer products, 2 for fractional units/syringes. */
  decimalScale?: number;
  /** Optional noun rendered after the number, e.g. "units" / "syringes". */
  unitLabel?: string;
  /** Tooltip shown on the disabled decrement control (i.e. at the minimum). */
  minReachedTooltip?: string;
  /** Tooltip shown on the disabled increment control (i.e. at the maximum). */
  maxReachedTooltip?: string;
  /** Shows a small spinner beside the control while a save is in flight. */
  saving?: boolean;
  disabled?: boolean;
  /** Accessible label prefix, e.g. "Botox quantity". */
  ariaLabel: string;
}

/** Round to a fixed number of decimals, avoiding binary-float dust from
 *  fractional (0.5) steps. */
function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * Compact inline +/- quantity stepper used on transaction line-item rows.
 * Presentational only — debounced persistence is owned by the caller (see
 * useDebouncedLineItemQuantity). Supports fractional steps (per-syringe/unit
 * services) and integer steps (products).
 */
export default function InlineQuantityStepper({
  value,
  onChange,
  min,
  max,
  step,
  decimalScale = 0,
  unitLabel,
  minReachedTooltip,
  maxReachedTooltip,
  saving = false,
  disabled = false,
  ariaLabel,
}: InlineQuantityStepperProps) {
  const decrementDisabled =
    disabled || roundTo(value - step, decimalScale) < min;
  // Disable at/above the cap (not one-step-before) so a final partial step can
  // land exactly on a fractional max — handleIncrement clamps the overshoot.
  const incrementDisabled =
    disabled || (max !== undefined && roundTo(value, decimalScale) >= max);

  const handleDecrement = () => {
    if (decrementDisabled) return;
    onChange(Math.max(min, roundTo(value - step, decimalScale)));
  };

  const handleIncrement = () => {
    if (incrementDisabled) return;
    onChange(clamp(roundTo(value + step, decimalScale)));
  };

  // Clamp a value into [min, max] (max optional).
  function clamp(num: number): number {
    const lower = Math.max(min, num);
    return max !== undefined ? Math.min(max, lower) : lower;
  }

  // Emit numeric edits as the user types; empty/partial input is ignored so
  // the value doesn't collapse mid-edit. Clamp to [min, max] on the way out so
  // a typed value outside the bounds never propagates a doomed save.
  const handleInput = (val: number | string) => {
    if (val === "" || val === null || val === undefined) return;
    const num = typeof val === "number" ? val : parseFloat(val);
    if (!Number.isFinite(num)) return;
    onChange(clamp(roundTo(num, decimalScale)));
  };

  const decrementButton = (
    <ActionIcon
      variant="subtle"
      size="sm"
      className={styles.iconButton}
      onClick={handleDecrement}
      disabled={decrementDisabled}
      aria-label={`Decrease ${ariaLabel}`}
    >
      <IconMinus size={15} />
    </ActionIcon>
  );

  const incrementButton = (
    <ActionIcon
      variant="subtle"
      size="sm"
      className={styles.iconButton}
      onClick={handleIncrement}
      disabled={incrementDisabled}
      aria-label={`Increase ${ariaLabel}`}
    >
      <IconPlus size={15} />
    </ActionIcon>
  );

  return (
    <Box className={styles.wrapper}>
      <Box className={styles.stepper}>
        {decrementDisabled && minReachedTooltip ? (
          <Tooltip label={minReachedTooltip} multiline w={260} withArrow>
            {/* Box wrapper keeps hover working over the disabled control. */}
            <Box component="span" className={styles.disabledWrap}>
              {decrementButton}
            </Box>
          </Tooltip>
        ) : (
          decrementButton
        )}

        <NumberInput
          value={value}
          onChange={handleInput}
          min={min}
          max={max}
          step={step}
          decimalScale={decimalScale}
          clampBehavior="strict"
          hideControls
          allowNegative={false}
          disabled={disabled}
          size="xs"
          aria-label={ariaLabel}
          classNames={{ input: styles.input }}
          w={decimalScale > 0 ? 46 : 34}
        />

        {unitLabel && (
          <Box component="span" className={styles.unitLabel}>
            {unitLabel}
          </Box>
        )}

        {incrementDisabled && maxReachedTooltip ? (
          <Tooltip label={maxReachedTooltip} multiline w={260} withArrow>
            {/* Box wrapper keeps hover working over the disabled control. */}
            <Box component="span" className={styles.disabledWrap}>
              {incrementButton}
            </Box>
          </Tooltip>
        ) : (
          incrementButton
        )}
      </Box>

      {saving && (
        <Loader size={14} color="var(--brand-teal)" aria-label="Saving" />
      )}
    </Box>
  );
}
